import React from 'react';
import {
  Box, IconButton, Paper, Skeleton, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, Tabs, Tooltip, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StatusChip from '../../Common/StatusChip';

const SKELETON_ROWS = 5;
const ROLE_FILTERS = ['all', 'admin', 'user'];

const UserTable = ({
  users, loading, onEdit, onDelete,
  roleFilter, onRoleFilterChange,
  page, rowsPerPage, total, onPageChange, onRowsPerPageChange,
  currentUserEmail,
}) => (
  <Box>
    <Tabs
      value={roleFilter}
      onChange={(_, v) => onRoleFilterChange(v)}
      sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
    >
      {ROLE_FILTERS.map((f) => (
        <Tab key={f} value={f} label={f} sx={{ textTransform: 'capitalize' }} />
      ))}
    </Tabs>

    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead sx={{ bgcolor: 'grey.50' }}>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Access Status</TableCell>
            <TableCell>Active</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="85%" /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={48} height={20} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={72} height={22} /></TableCell>
                  <TableCell><Skeleton variant="text" width={24} /></TableCell>
                  <TableCell align="right" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Skeleton variant="circular" width={28} height={28} />
                    <Skeleton variant="circular" width={28} height={28} />
                  </TableCell>
                </TableRow>
              ))
            : users.map((u) => (
                <TableRow key={u.userId ?? u.id} hover>
                  <TableCell>{u.name || '—'}</TableCell>
                  <TableCell>{u.companyEmail}</TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: u.role === 'admin' ? 'primary.100' : 'grey.100',
                        color: u.role === 'admin' ? 'primary.dark' : 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'capitalize',
                      }}
                    >
                      {u.role || 'user'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={u.access_status || 'none'} />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      sx={{
                        color: u.status === 'active' ? 'success.main' : 'error.main',
                        fontWeight: 600,
                      }}
                    >
                      {u.status === 'active' ? 'Yes' : 'No'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={u.companyEmail === currentUserEmail ? 'Cannot edit your own account' : 'Edit'}>
                      <span>
                        <IconButton size="small" onClick={() => onEdit(u)} disabled={u.companyEmail === currentUserEmail}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={u.companyEmail === currentUserEmail ? 'Cannot deactivate your own account' : 'Deactivate'}>
                      <span>
                        <IconButton size="small" color="error" onClick={() => onDelete(u)} disabled={u.companyEmail === currentUserEmail}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>
    </TableContainer>
    <TablePagination
      component="div"
      count={total}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={[5, 10, 25]}
    />
  </Box>
);

export default UserTable;
